import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";
import ComplianceFrameworkSettingsService from "src/modules/data-protection/apis/ComplianceFrameworkSettingsService";
import TaskControlsService from "src/modules/data-protection/apis/TaskControlsService";
import { ControlsFrameworks } from "src/modules/data-protection/constants";
import { useCallback, useState } from "react";
import { EMPTY_LIST } from "src/common/constants";
import AsyncReactSelect from "src/components/AsyncReactSelect";
import { formatControlNumber } from "src/modules/data-protection/helpers/Controls";

const ISMSControls = ({
  t,
  lng,
  taskId,
  defaultValues,
  controlsOptions,
  isReadOnly,
  removedControlsByFramework
}) => {
  const mappedDefaultValues = {};
  Object.keys(defaultValues || {}).forEach((frameworkName) => {
    mappedDefaultValues[frameworkName] = defaultValues[frameworkName].map(
      (defauleSelected) => {
        return {
          value: defauleSelected.id,
          label: `${formatControlNumber(defauleSelected.control_number, frameworkName)}. ${t(
            defauleSelected.title
          )}`,
        };
      }
    );
  });

  
  const [selectedValues, setSelectedValues] = useState(mappedDefaultValues);

  const taskControlsService = TaskControlsService.getInstance();

  const ismsAccessibleControlsQuery = useQuery({
    queryKey: ["gdpr-fetch-isms-accessible-controls", controlsOptions],
    queryFn: async () => {
      const service = ComplianceFrameworkSettingsService.getInstance();

      return await service.fetchAccessibleControls({
        ...controlsOptions,
      });
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while retrieving the controls list."), {
        type: "error",
      });
    },
  });

  const { mutate: handleRemoveControl } = useMutation({
    mutationFn: async ({ control }) => {
      return await taskControlsService.removeControl(taskId, {
        control: control.value.toString(),
      });
    },
    onSuccess: (_response, { control, framework }) => {
      toast(t("The control was successfully removed."), {
        type: "success",
      });

      setSelectedValues((prev) => {
        return {
          ...prev,
          [framework]: [
            ...prev[framework]?.filter((item) => item.value !== control.value),
          ],
        };
      });
    },
    onError: () => {
      toast(t("Failed to remove the control. Please try again."), {
        type: "error",
      });
    },
  });

  const { mutate: handleAddControl } = useMutation({
    mutationFn: async ({ control }) => {
      return await taskControlsService.addControl(taskId, {
        control: control.value.toString(),
      });
    },
    onSuccess: (_response, { framework, control }) => {
      toast(t("The selected control was successfully added."), {
        type: "success",
      });

      setSelectedValues((prev) => {
        return {
          ...prev,
          [framework]: [...(prev[framework] || []), { ...control }],
        };
      });
    },
    onError: () => {
      toast(t("An error occurred while adding the control."), {
        type: "error",
      });
    },
  });

  //   const { accessible_frameworks = [] } = ismsAccessibleControlsQuery.data || {};
  const { accessible_frameworks = [] } = ismsAccessibleControlsQuery.data || {};


  const removedFrameworks = Object.keys(removedControlsByFramework).filter(
    (frameworkKey) => removedControlsByFramework[frameworkKey]?.length > 0
  );

  const visibleFrameworks = Array.from(
    new Set([...accessible_frameworks, ...removedFrameworks])
  );

  const controlsQueries = useQueries({
    queries: visibleFrameworks.map((framework) => ({
      queryKey: [`gdpr-controls`, lng, framework],
      queryFn: async () => {
        const service = TaskControlsService.getInstance();

        const response = await service.fetchControls({
          type: framework,
        });

        return (response || []).map((control) => {
          return {
            value: control.id,
            label: `${formatControlNumber(control.control_number, framework)}. ${t(control.title)}`,
            baseLabel: control.title,
          };
        });
      },
      // enabled: ismsAccessibleControlsQuery.isFeched && ismsAccessibleControlsQuery.isSuccess,
      enabled: false,
    })),
  });

  const controlsQueriesResults = visibleFrameworks.map(
    (frameworkKey, index) => ({
      ...controlsQueries[index],
      frameworkName: frameworkKey,
      isRemovedFramework: removedControlsByFramework[frameworkKey]?.length > 0,
      removedControls: removedControlsByFramework[frameworkKey] || [],
    })
  );

  const loadControlsOptions = useCallback(async (params) => {
    const { controlQuery } = params;

    if (!controlQuery) throw new Error();

    return {
      options: (await controlQuery.refetch())?.data || [],
      pageIndex: 1,
      pageSize: 100,
      totalCount: 100,
    };
  }, []);

  if (ismsAccessibleControlsQuery.isFetching) {
    return (
      <Row>
        <Col sm="6">
          <div
            className="dt-field dt-skeleton dt-select-list"
            style={{ marginBottom: 16 }}
          ></div>
        </Col>

        <Col sm="6">
          <div
            className="dt-field dt-skeleton dt-select-list"
            style={{ marginBottom: 16 }}
          ></div>
        </Col>
      </Row>
    );
  }

  return (
    <Row>
      {controlsQueriesResults.map((controlQuery) => {
        const controleFrameworkInfo = ControlsFrameworks.find(
          (framework) => framework.key === controlQuery.frameworkName
        );

        const itemSelectedControls =
          selectedValues[controlQuery.frameworkName] || [];

        const removedControls = controlQuery.removedControls || [];
        const isRemovedFramework = removedControls.length > 0;

        return (
          <Col key={controlQuery.frameworkName} sm="12" md="6" className="mb-5">
            <div
              style={{
                position: "relative",
                border: "1px solid #d6d6d6",
                borderRadius: "6px",
                padding: "28px 14px 14px 14px",
                height: "220px",
                minHeight: "220px",
                maxHeight: "220px",
                display: "flex",
                flexDirection: "column",
                backgroundColor: isRemovedFramework ? "#f8f9fa" : "#fff",
                opacity: isRemovedFramework ? 0.85 : 1,
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: "-14px",
                  left: "14px",
                  backgroundColor: isRemovedFramework ? "#6c757d" : "#007a0c",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "14px",
                  padding: "4px 10px",
                  borderRadius: "4px",
                  lineHeight: "18px",
                  zIndex: 1,
                }}
              >
                {t(controleFrameworkInfo?.title)}
              </span>

              <div style={{ flexShrink: 0 }}>
                <AsyncReactSelect
                  loadOptions={loadControlsOptions}
                  settings={{
                    queryKey: "title",
                    loadOptionsErrorMessage: "Failed to fetch",
                    paramsToInject: {
                      controlQuery,
                    },
                  }}
                  value={null}
                  onChange={(e) => {
                    if (!e || isRemovedFramework) return;

                    handleAddControl({
                      framework: controlQuery.frameworkName,
                      control: e,
                    });
                  }}
                  placeholder={
                    isRemovedFramework
                      ? t("Framework removed")
                      : t("Select a control")
                  }
                  isDisabled={isReadOnly || isRemovedFramework}
                />
              </div>

              <hr
                style={{
                  margin: "12px 0",
                  flexShrink: 0,
                }}
              />

              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  paddingRight: "6px",
                }}
              >
                {itemSelectedControls.length > 0 &&
                  itemSelectedControls.map((control) => (
                    <div
                      key={control.value}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "10px",
                        fontSize: "14px",
                        color: isRemovedFramework ? "#6c757d" : "#444",
                        marginBottom: "12px",
                        lineHeight: "20px",
                      }}
                    >
                      <span style={{ wordBreak: "break-word" }}>
                        {control.label}
                      </span>

                      {!isReadOnly && !isRemovedFramework && (
                        <span
                          onClick={() => {
                            handleRemoveControl({
                              framework: controlQuery.frameworkName,
                              control,
                            });
                          }}
                          style={{
                            color: "#ff2b2b",
                            fontSize: "24px",
                            lineHeight: "18px",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        >
                          ×
                        </span>
                      )}
                    </div>
                  ))}

                {removedControls.length > 0 &&
                  removedControls.map((control) => (
                    <div
                      key={`removed-${control.id}`}
                      style={{
                        fontSize: "14px",
                        color: "#6c757d",
                        marginBottom: "12px",
                        lineHeight: "20px",
                      }}
                    >
                      <div style={{ wordBreak: "break-word" }}>
                        {formatControlNumber(control.control_number, controlQuery.frameworkName)}. {t(control.title)}
                      </div>

                      <small style={{ color: "#dc3545" }}>
                        {t(control.removed_reason)}
                      </small>
                    </div>
                  ))}

                {itemSelectedControls.length === 0 &&
                  removedControls.length === 0 && (
                    <div
                      style={{
                        color: "#999",
                        fontSize: "14px",
                      }}
                    >
                      {t(EMPTY_LIST)}
                    </div>
                  )}
              </div>
            </div>
          </Col>
        );
      })}
    </Row>
  );

};

export default withNamespaces()(ISMSControls);
